import React, { ReactNode, useCallback } from 'react';
import { AutoSizer } from 'react-virtualized';
import { useResizeDetector } from 'react-resize-detector';
import { OnResizeCallback } from 'react-resize-detector/build/types/types';
import useMinChartHeight from './utils/useMinChartHeight';
import { useCurrentUrl } from '../../utils/routing';
import { ChartMode } from './types';
import { useAppDispatch } from '../../redux/hooks';
import { handleResizeAnalysisAction } from '../../redux/slices/analyses';

interface IProps {
  analysisId: string;
  mode: ChartMode;
  children: ReactNode;
}

const ResponsiveChartWrapper = ({ analysisId, mode, children }: IProps) => {
  const dispatch = useAppDispatch();

  const onResize: OnResizeCallback = useCallback(
    (newChartWidth) => {
      // Handle undefined width from OnResizeCallback
      if (!newChartWidth) {
        return;
      }

      dispatch(
        handleResizeAnalysisAction({
          id: analysisId,
          newChartWidth,
        }),
      );
    },
    [analysisId, dispatch],
  );

  const { ref } = useResizeDetector({
    handleWidth: true,
    handleHeight: false,
    refreshMode: 'throttle',
    refreshRate: 1000,
    onResize,
  });

  // disabling default browser context menu
  // in order to make our custom context menus (expand, create cohort, etc.)
  // be a single UI element
  const handleContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const { isReportInUrl } = useCurrentUrl();
  const minChartHeight = useMinChartHeight(mode);

  // Special case: render chart as is on Report page
  if (isReportInUrl && mode !== ChartMode.Compact) {
    return (
      <div
        ref={ref}
        onContextMenu={handleContextMenu}
        style={{ flex: '1 1 auto', height: minChartHeight }}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      onContextMenu={handleContextMenu}
      style={{ flex: '1 1 auto', minHeight: minChartHeight }}
    >
      <AutoSizer disableWidth={true}>
        {({ height }) => (
          <div style={{ width: '100%', height }}>{children}</div>
        )}
      </AutoSizer>
    </div>
  );
};

export default ResponsiveChartWrapper;
